<script>
// "History" tab of the Upload Projects modal: every import of the account,
// newest first (GET /properties/import), and a click-through to one job's
// per-row results. Refreshes itself while any listed job is still queued or
// processing, since imports run one at a time and can wait a while. Each
// row can also download the workbook it was started with.
import { format } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import {
  listImportJobs,
  getImportJob,
  getImportFile,
} from '../../../../api/realEstate/importJobs';
import ImportJobResults from './ImportJobResults.vue';
import { SELECT_CLASS } from '../constants';
import { downloadBlob } from '../helpers';

const PAGE_SIZE = 10;
const REFRESH_MS = 5000;
const ACTIVE_STATUSES = ['pending', 'queued', 'processing'];
const STATUS_FILTERS = ['queued', 'processing', 'completed', 'failed'];

const STATUS_CLASS = {
  pending: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  queued: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  processing: 'bg-woot-50 text-woot-700 dark:bg-woot-900/40 dark:text-woot-300',
  completed:
    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
};

export default {
  name: 'ImportHistory',
  components: { ImportJobResults },
  data() {
    return {
      selectClass: SELECT_CLASS,
      statusFilters: STATUS_FILTERS,
      jobs: [],
      total: 0,
      page: 1,
      status: '',
      isLoading: false,
      loadError: false,
      openJob: null,
      isOpeningJob: false,
      downloadingJobId: null,
      refreshTimer: null,
    };
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.total / PAGE_SIZE));
    },
    hasActiveJobs() {
      return this.jobs.some(job => ACTIVE_STATUSES.includes(job.status));
    },
  },
  watch: {
    status() {
      this.page = 1;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer);
  },
  methods: {
    // `quiet` = a background refresh: no spinner, and a failure keeps the
    // rows already on screen instead of replacing them with an error.
    async load({ quiet = false } = {}) {
      clearTimeout(this.refreshTimer);
      if (!quiet) this.isLoading = true;
      try {
        const { data } = await listImportJobs({
          page: this.page,
          pageSize: PAGE_SIZE,
          status: this.status,
        });
        this.jobs = data.results;
        this.total = data.total;
        this.loadError = false;
      } catch (err) {
        if (!quiet) this.loadError = true;
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoading = false;
        if (this.hasActiveJobs) {
          this.refreshTimer = setTimeout(
            () => this.load({ quiet: true }),
            REFRESH_MS
          );
        }
      }
    },
    goToPage(page) {
      this.page = page;
      this.load();
    },
    async openResults(job) {
      this.isOpeningJob = true;
      try {
        const { data } = await getImportJob(job.job_id);
        this.openJob = data;
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isOpeningJob = false;
      }
    },
    closeResults() {
      this.openJob = null;
    },
    // The response is a Blob even on errors, so the 404 is told apart by
    // status rather than by the error code in its body.
    async downloadFile(job) {
      this.downloadingJobId = job.job_id;
      try {
        const { data } = await getImportFile(job.job_id);
        downloadBlob(job.file_name, data);
      } catch (err) {
        const gone = err.response && err.response.status === 404;
        useAlert(
          this.$t(
            gone
              ? 'REAL_ESTATE.BULK_IMPORT.HISTORY_FILE_GONE'
              : 'REAL_ESTATE.BULK_IMPORT.HISTORY_DOWNLOAD_ERROR'
          )
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.downloadingJobId = null;
      }
    },
    canDownload(job) {
      return job.file_available !== false;
    },
    // Jobs from before modes existed have no `mode`: they only merged.
    modeLabel(mode) {
      return mode === 'replace'
        ? this.$t('REAL_ESTATE.BULK_IMPORT.MODE_REPLACE')
        : this.$t('REAL_ESTATE.BULK_IMPORT.MODE_MERGE_SHORT');
    },
    statusLabel(status) {
      return this.$t(`REAL_ESTATE.BULK_IMPORT.STATUS_${status.toUpperCase()}`);
    },
    statusClass(status) {
      return STATUS_CLASS[status] || STATUS_CLASS.queued;
    },
    formatDate(iso) {
      return iso ? format(new Date(iso), 'd MMM yyyy, h:mm a') : '—';
    },
    uploaderName(job) {
      return job.uploaded_by && job.uploaded_by.name
        ? job.uploaded_by.name
        : '—';
    },
    isFinished(job) {
      return !ACTIVE_STATUSES.includes(job.status);
    },
  },
};
</script>

<template>
  <div>
    <div v-if="openJob" class="space-y-3">
      <woot-button
        variant="clear"
        size="small"
        icon="chevron-left"
        @click="closeResults"
      >
        {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_BACK') }}
      </woot-button>
      <p class="mb-0 text-sm font-medium text-slate-900 dark:text-slate-100">
        {{ openJob.file_name }}
      </p>
      <ImportJobResults :job="openJob" />
    </div>

    <div v-else class="space-y-3">
      <div class="flex items-center justify-between gap-3">
        <select v-model="status" :class="selectClass" class="!w-44">
          <option value="">
            {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_FILTER_ALL') }}
          </option>
          <option v-for="value in statusFilters" :key="value" :value="value">
            {{ statusLabel(value) }}
          </option>
        </select>
        <woot-button
          variant="clear"
          size="small"
          icon="arrow-clockwise"
          :is-loading="isLoading"
          @click="load()"
        />
      </div>

      <p v-if="loadError" class="mb-0 text-sm text-red-600 dark:text-red-400">
        {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_ERROR') }}
      </p>
      <p
        v-else-if="!isLoading && !jobs.length"
        class="mb-0 py-6 text-center text-sm text-slate-500 dark:text-slate-400"
      >
        {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_EMPTY') }}
      </p>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-xs border-collapse">
          <thead class="text-left text-slate-500 dark:text-slate-400">
            <tr class="border-b border-slate-200 dark:border-slate-700">
              <th class="py-1.5 pr-3 font-medium">
                {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_COLUMN_FILE') }}
              </th>
              <th class="py-1.5 pr-3 font-medium">
                {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_COLUMN_UPLOADED_BY') }}
              </th>
              <th class="py-1.5 pr-3 font-medium">
                {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_COLUMN_DATE') }}
              </th>
              <th class="py-1.5 pr-3 font-medium">
                {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_COLUMN_MODE') }}
              </th>
              <th class="py-1.5 pr-3 font-medium">
                {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_COLUMN_STATUS') }}
              </th>
              <th class="py-1.5 pr-3 font-medium">
                {{ $t('REAL_ESTATE.BULK_IMPORT.HISTORY_COLUMN_RESULTS') }}
              </th>
              <th class="py-1.5" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="job in jobs"
              :key="job.job_id"
              class="border-b border-slate-100 dark:border-slate-800 align-top"
              :class="
                isFinished(job)
                  ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800'
                  : ''
              "
              @click="isFinished(job) && !isOpeningJob && openResults(job)"
            >
              <td
                class="py-2 pr-3 font-medium text-slate-900 dark:text-slate-100 break-all"
              >
                {{ job.file_name }}
              </td>
              <td class="py-2 pr-3 text-slate-600 dark:text-slate-300">
                {{ uploaderName(job) }}
              </td>
              <td
                class="py-2 pr-3 whitespace-nowrap text-slate-600 dark:text-slate-300"
              >
                {{ formatDate(job.created_at) }}
              </td>
              <td
                class="py-2 pr-3 whitespace-nowrap"
                :class="
                  job.mode === 'replace'
                    ? 'text-red-600 dark:text-red-400 font-medium'
                    : 'text-slate-600 dark:text-slate-300'
                "
              >
                {{ modeLabel(job.mode) }}
              </td>
              <td class="py-2 pr-3">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
                  :class="statusClass(job.status)"
                >
                  {{ statusLabel(job.status) }}
                </span>
              </td>
              <td class="py-2 pr-3 text-slate-600 dark:text-slate-300">
                <template v-if="job.status === 'completed'">
                  {{
                    $t(
                      job.mode === 'replace'
                        ? 'REAL_ESTATE.BULK_IMPORT.HISTORY_COUNTS_REPLACE'
                        : 'REAL_ESTATE.BULK_IMPORT.HISTORY_COUNTS',
                      {
                        created: job.created,
                        updated: job.updated,
                        skipped: job.skipped,
                        deleted: job.deleted || 0,
                        failed: job.failed,
                      }
                    )
                  }}
                </template>
                <template v-else>—</template>
              </td>
              <td class="py-1 text-right">
                <woot-button
                  variant="clear"
                  size="tiny"
                  color-scheme="secondary"
                  icon="arrow-download"
                  :title="
                    canDownload(job)
                      ? $t('REAL_ESTATE.BULK_IMPORT.HISTORY_DOWNLOAD')
                      : $t('REAL_ESTATE.BULK_IMPORT.HISTORY_FILE_GONE')
                  "
                  :disabled="!canDownload(job) || downloadingJobId !== null"
                  :is-loading="downloadingJobId === job.job_id"
                  @click.stop="downloadFile(job)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="pageCount > 1"
        class="flex items-center justify-end gap-2 text-xs text-slate-500 dark:text-slate-400"
      >
        <woot-button
          variant="clear"
          size="small"
          :disabled="page <= 1 || isLoading"
          @click="goToPage(page - 1)"
        >
          {{ $t('REAL_ESTATE.BULK_IMPORT.PAGE_PREV') }}
        </woot-button>
        <span>
          {{
            $t('REAL_ESTATE.BULK_IMPORT.PAGE_OF', { page, pages: pageCount })
          }}
        </span>
        <woot-button
          variant="clear"
          size="small"
          :disabled="page >= pageCount || isLoading"
          @click="goToPage(page + 1)"
        >
          {{ $t('REAL_ESTATE.BULK_IMPORT.PAGE_NEXT') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
